/* screens/auth/SignIn.jsx — Вход (S12, /signin).
   Email+пароль, «Оставаться в системе», anti-enumeration (одинаковый текст ошибки),
   ветка needsVerify (email_not_verified → ссылка повторной отправки). После успеха —
   pending_invite_token → /invite/accept; затем хэндофф демо-стенда (заявка в
   sessionStorage): новый аккаунт с онбординг-флоу из /me → на главную (откроется
   мастер); обычный существующий аккаунт (флоу нет — усыновление убрано,
   харденинг «много демо») → /chat.
   Сессия: authApi.login (боевой клиент) → useAuth().login(res.data) поднимает workspaces;
   logout/me там же. Повторная отправка письма — authReal.resendVerify.
   ХЕНДОФФ → frontend/src/pages/auth/SignIn.jsx (данные через AuthContext). */

import React from 'react';
import { useAuth } from '../../app/providers/AuthProvider.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Checkbox, Field, PasswordInput, TextInput } from '../../components/ui/Field.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { ScreenHeader } from '../../components/ui/Metric.jsx';
import { markDoneLocal } from '../../app/onboarding/OnboardingProvider.jsx';
import * as authApi from '../../lib/api/auth.js';
import { DEMO_SIGNUP_STORAGE_KEY } from '../../lib/api/demo.js';
import { authReal } from '../../lib/api/index.js';
import { parseDemoSignupStorage } from '../../lib/demoLead.js';
import { RR } from '../../lib/router.jsx';
import { safeNext } from '../../lib/safeNext.js';

  const { useState, useEffect } = React;
  const { useNavigate, useSearchParams, Link } = RR;
  const PENDING_INVITE_KEY = 'pending_invite_token';
  const PENDING_PAY_LINK_KEY = 'pending_payment_link_token';
  // Подарочный код, сохранённый экраном активации перед уходом на вход.
  const PENDING_GIFT_KEY = 'pending_gift_code';
  // Куда вернуть юзера после входа (лендинг курса Академии для анонима, фикс №16/A11).
  // Зеркалит PENDING_INVITE_KEY: переживает раунд email-verify через sessionStorage.
  const ACADEMY_NEXT_KEY = 'restorium.academy.next';

  function SignIn() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [sp] = useSearchParams();
    const passwordResetDone = sp.get('password_reset') === 'done';
    // next из URL приоритетнее сохранённого; валидный ранний `?next=` кладём в sessionStorage,
    // чтобы он пережил переход на /email-sent (если аккаунт ещё не подтверждён).
    const nextFromUrl = safeNext(sp.get('next'));
    useEffect(() => {
      if (nextFromUrl) {
        try { sessionStorage.setItem(ACADEMY_NEXT_KEY, nextFromUrl); } catch (e) {}
      }
    }, [nextFromUrl]);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [remember, setRemember] = useState(true);
    const [errors, setErrors] = useState({});
    const [topError, setTopError] = useState(null);
    const [needsVerify, setNeedsVerify] = useState(false);
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
      e.preventDefault();
      const errs = {};
      if (!/^\S+@\S+\.\S+$/.test(email)) errs.email = 'Введите корректный email.';
      if (!password) errs.password = 'Введите пароль.';
      setErrors(errs);
      setTopError(null); setNeedsVerify(false);
      if (Object.keys(errs).length) return;

      setLoading(true);
      try {
        // remember управляет временем жизни сессии Django — true → persistent cookie (30 дней),
        // false → session-cookie (истекает при закрытии браузера). Уходит в тело /login/.
        // authApi.login возвращает {ok,status,data} (НЕ бросает) — ветвимся по res.ok.
        const res = await authApi.login({ email, password, remember });
        if (!res.ok) {
          const code = res.data && res.data.code;
          const detail = (res.data && res.data.detail) || '';
          // 403 email_not_verified → показываем ссылку повторной отправки;
          // 401 invalid_credentials (anti-enumeration: один текст) и прочее → topError.
          if (code === 'email_not_verified') { setNeedsVerify(true); setTopError(detail || 'Email не подтверждён.'); }
          else setTopError(detail || 'Не удалось войти. Попробуйте ещё раз.');
          setLoading(false);
          return;
        }
        // Хэндофф демо-стенда: анкета лежит в sessionStorage. Флоу мастера
        // РОЖДАЕТСЯ только при регистрации нового аккаунта и приезжает в res.data
        // (/me) ключом onboarding_flow. Усыновление по signup-токену убрано
        // (харденинг «много демо»): существующий аккаунт свежее демо больше не
        // получает — ведём его в обычный /chat. Ключ анкеты израсходован/неактуален.
        let demo = null;
        try { demo = parseDemoSignupStorage(sessionStorage.getItem(DEMO_SIGNUP_STORAGE_KEY)); }
        catch { demo = null; }
        // Мастер откроется только у нового аккаунта с уже созданным флоу.
        const willOpenFlow = !!(demo && res.data && res.data.onboarding_flow);
        // Защёлка квеста ДО поднятия контекста — ТОЛЬКО если откроется мастер:
        // иначе автостарт (400мс) мелькнёт квестом рядом с мастером (QA ONB-04
        // A8/A9). Обычному аккаунту без флоу штатный квест НЕ глушим.
        if (willOpenFlow) markDoneLocal();
        // res.data — объект пользователя (UserSerializer). Поднимаем сессию пространств в AuthContext.
        await login(res.data);
        const pending = sessionStorage.getItem(PENDING_INVITE_KEY);
        // Приглашение приоритетнее next: завершаем accept-флоу, next остаётся в sessionStorage.
        if (pending) { sessionStorage.removeItem(PENDING_INVITE_KEY); navigate('/invite/accept/' + pending); return; }
        // Тот же приём для именной ссылки на оплату (/pay/:token): PayByLink положил токен
        // перед уходом на вход. Без возврата клиент окажется в чате и счёт потеряет.
        const pendingPay = sessionStorage.getItem(PENDING_PAY_LINK_KEY);
        if (pendingPay) { sessionStorage.removeItem(PENDING_PAY_LINK_KEY); navigate('/pay/' + pendingPay); return; }
        // То же для подарочного сертификата: человек пришёл активировать код,
        // и высадить его в чат значило бы потерять подарок, за который заплачено.
        const pendingGift = sessionStorage.getItem(PENDING_GIFT_KEY);
        if (pendingGift) { sessionStorage.removeItem(PENDING_GIFT_KEY); navigate('/gift/' + pendingGift); return; }
        // Безопасный локальный next (URL приоритетнее сохранённого) → возврат на лендинг курса.
        // Идёт ПЕРЕД демо-веткой: `next` появляется, только когда юзер пришёл с гейтованной
        // страницы, а протухший ключ демо-анкеты иначе съел бы его и увёл в /chat.
        const next = nextFromUrl || safeNext(sessionStorage.getItem(ACADEMY_NEXT_KEY));
        if (next) { try { sessionStorage.removeItem(ACADEMY_NEXT_KEY); } catch (e) {} navigate(next); return; }
        if (demo) {
          // Анкета израсходована/неактуальна — чистим ключ безусловно.
          try { sessionStorage.removeItem(DEMO_SIGNUP_STORAGE_KEY); } catch { /* noop */ }
          // Новый аккаунт с флоу → на главную (откроется мастер); обычный
          // существующий аккаунт без флоу → в чат, без онбординга.
          navigate(willOpenFlow ? '/' : '/chat');
          return;
        }
        // ДЫРА закрыта: у бэка нет флага is_new — ветка onboarding убрана; после входа сразу в /chat.
        // (Маршрут /onboarding + OnboardingWizard остаются, но из входа не триггерятся.)
        navigate('/chat');
      } catch (err) {
        setTopError(err.message || 'Не удалось войти. Попробуйте ещё раз.');
        setLoading(false);
      }
    };

    return (
      <form className="auth-form stack stack-16" onSubmit={submit} noValidate>
        <ScreenHeader title="Вход" className="screen-header--accent" />

        {passwordResetDone && !topError && (
          <div className="callout callout--ok" role="status">
            <Icon name="check-circle" size={16} /><span>Пароль обновлён. Войдите с новым паролем.</span>
          </div>
        )}

        {topError && (
          <div className="callout callout--bad" role="alert">
            <Icon name="alert-circle" size={16} />
            <div className="stack stack-6">
              <span>{topError}</span>
              {needsVerify && (
                <button type="button" className="link-btn auth-form__resend"
                  onClick={async () => {
                    // фактически отправляем письмо до перехода — иначе EmailSent заявит «отправили», а API не вызван (фикс №18)
                    try { await authReal.resendVerify(email); } catch (e) {}
                    navigate('/email-sent', { state: { email, mode: 'verify' } });
                  }}>
                  Отправить письмо повторно
                </button>
              )}
            </div>
          </div>
        )}

        <div className="stack stack-12">
          <Field label="E-mail" htmlFor="si-email" error={errors.email}>
            <TextInput id="si-email" type="email" value={email} autoComplete="username"
              invalid={errors.email ? true : undefined} placeholder="you@restaurant.ru"
              onChange={(e) => setEmail(e.target.value)} />
          </Field>
          <Field label={
            <span className="auth-form__label-row">Пароль
              <Link to="/forgot" className="link auth-form__forgot">Забыли?</Link>
            </span>
          } htmlFor="si-pass" error={errors.password}>
            <PasswordInput id="si-pass" value={password} autoComplete="current-password"
              invalid={errors.password ? true : undefined} placeholder="Ваш пароль"
              onChange={(e) => setPassword(e.target.value)} />
          </Field>
          <Checkbox label="Оставаться в системе" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
        </div>

        <Button type="submit" variant="primary" size="lg" loading={loading} className="full">Войти</Button>

        <p className="auth-form__alt t-body text-dim">
          Нет аккаунта? <Link to={nextFromUrl ? '/signup?next=' + encodeURIComponent(nextFromUrl) : '/signup'} className="link">Создать</Link>
        </p>
      </form>
    );
  }

export { SignIn };
